import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, CardContent, Typography, Button, CircularProgress, Box, Snackbar } from '@mui/material';
import { getEvents } from '../api';
import { getShareIdForEvent } from '../utils/favorites';
import { getBlogForEvent } from '../config/blogMapping';

function cleanDescription(description = '') {
  return description
    .split('\n')
    .filter(line =>
      !line.includes('Map') &&
      !line.includes('Highlight this date') &&
      !line.includes('Highlight all dates') &&
      !line.includes('Link to this event') &&
      !line.includes('Update this event') &&
      !line.includes('If anything here is inaccurate')
    )
    .join('\n');
}

function EventDetail() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getEvents();
        const allEvents = (data && Array.isArray(data.data)) ? data.data : [];

        // Mirror the same filtering as EventList for "No event here"
        const filteredData = allEvents.filter(ev =>
          ev['Event Name'] && !ev['Event Name'].includes('No event')
        );

        const found = filteredData.find(ev => getShareIdForEvent(ev) === shareId);

        if (!found) {
          setError('This event is not available in the current dataset.');
          setEvent(null);
        } else {
          setEvent(found);
        }
      } catch (err) {
        console.error('Failed to load event details', err);
        setError('Failed to load event details. Please try again later.');
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchEvent();
    } else {
      setError('No event specified.');
      setLoading(false);
    }
  }, [shareId]);

  const blogInfo = useMemo(() => {
    if (!event) return null;
    return getBlogForEvent(event['Event Name']);
  }, [event]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = event ? event['Event Name'] : 'Event';

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch {
      // If Web Share fails, fall back to clipboard/prompt below.
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setShareMessage('Link copied to clipboard');
        setShareOpen(true);
        return;
      }
    } catch {
      // Ignore and fall through to prompt.
    }

    // Ultimate fallback: show a prompt so the user can copy manually.
    window.prompt('Copy this event link:', url);
  };

  const handleBack = () => {
    // If we have history within the app, go back; otherwise, fall back to list with current filters
    if (window.history.length > 1 && document.referrer && document.referrer.startsWith(window.location.origin)) {
      navigate(-1);
    } else {
      const search = location.search || '';
      navigate(`/${search}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography color="error" variant="body1" paragraph>
          {error}
        </Typography>
        <Button variant="outlined" onClick={handleBack}>
          Back to results
        </Button>
      </Container>
    );
  }

  if (!event) {
    return null;
  }

  const description = cleanDescription(event.Description || '');
  const locationText = event.Location === 'TBD' ? 'Location: TBD' : event.Location;

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Button variant="outlined" onClick={handleBack} sx={{ mb: 2 }}>
        Back to results
      </Button>

      <Card>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom>
            {event['Event Name']}
          </Typography>

          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            {event.Date} {event.Time}
          </Typography>

          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            {locationText}
          </Typography>

          <Box sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            <Typography variant="body1">
              {description}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
            {event.Details && (
              <Button
                href={event.Details}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color="primary"
              >
                Go to Site
              </Button>
            )}

            {blogInfo && (
              <Button
                href={blogInfo.blogUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#ffffff',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#d97706',
                  },
                  minWidth: 'fit-content'
                }}
                aria-label={blogInfo.blogTitle}
              >
                My Experience
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={handleShare}
            >
              Share this event
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={shareOpen}
        autoHideDuration={3000}
        onClose={() => setShareOpen(false)}
        message={shareMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
}

export default EventDetail;

